import moment from 'moment-timezone';

export interface TimestampStruct {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export enum Orientation {Horozontal, Vertical}

export class ReferenceLine {
  private static readonly HORIZONTAL_INDICATOR = 'H';
  private static readonly VERTICAL_INDICATOR = 'V';
  private static readonly SEPARATOR = ',';
  private static readonly TIMESTAMP_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

  private readonly label: string;
  private readonly orientation: Orientation;
  // Horizontal Reference Lines have value as a number, Vertical ones as a time stamp *without time zone*. No time zone information is
  // stored so that if the underlying timezone for the site changes, the 'nominal' value of the reference line does not. Therefore, to
  // convert the time stamp value to a timestamp a time zone must be provided
  private readonly value: number | TimestampStruct;

  private constructor(label: string, orientation: Orientation, value: number | TimestampStruct) {
    this.label = label;
    this.orientation = orientation;
    if (value !== undefined && value !== null) {
      if (this.orientation === Orientation.Horozontal) {
        if (typeof value === 'number') {
          this.value = value;
        } else {
          throw new Error('Horizontal ReferenceLine\'s value must be a number');
        }
      } else if (this.orientation === Orientation.Vertical) {
        if (typeof value === 'object') {
          this.value = Object.assign({}, value);
        } else {
          throw new Error('Vertical ReferenceLine\'s value must be an object');
        }
      } else {
        throw new Error('Unknown ReferenceLine orientation "' + orientation + '"');
      }
    } else {
      throw new Error('ReferenceLine must have a value');
    }
  }

  static buildVertical(label: string, value: TimestampStruct): ReferenceLine {
    return new ReferenceLine(label, Orientation.Vertical, value);
  }

  static buildHorizontal(label: string, value: number): ReferenceLine {
    return new ReferenceLine(label, Orientation.Horozontal, value);
  }

  static fromHashCode(hashCode: string) {
    const separatorIndex: number = hashCode.indexOf(ReferenceLine.SEPARATOR);
    if (separatorIndex < 0) {
      throw new Error('ReferenceLine hashCode parsing failed');
    }

    if (hashCode.startsWith(ReferenceLine.VERTICAL_INDICATOR)) {
      const ts = moment.utc(hashCode.substring(1, separatorIndex), ReferenceLine.TIMESTAMP_FORMAT);
      const label: string = hashCode.substring(separatorIndex + 1);
      const timestamp: TimestampStruct = {year: ts.year(), month: ts.month(), day: ts.date(), hour: ts.hour(), minute: ts.minute(), second: ts.second()};

      const referenceLine = new ReferenceLine(label, Orientation.Vertical, timestamp);
      if (referenceLine.hashCode !== hashCode) {
        throw new Error('ReferenceLine hashCode parsing failed');
      }
      return referenceLine;
    } else if (hashCode.startsWith(ReferenceLine.HORIZONTAL_INDICATOR)) {
      const value = Number(hashCode.substring(1, separatorIndex));
      if (isNaN(value)) {
        throw new Error('ReferenceLine hashCode parsing failed, value is not a number');
      }

      const label: string = hashCode.substring(separatorIndex + 1);
      const referenceLine = new ReferenceLine(label, Orientation.Horozontal, value);
      if (referenceLine.hashCode !== hashCode) {
        throw new Error('ReferenceLine hashCode parsing failed');
      }
      return referenceLine;
    } else {
      throw new Error(`ReferenceLine hashCode parsing failed, must start with either ${ReferenceLine.HORIZONTAL_INDICATOR} or ${ReferenceLine.VERTICAL_INDICATOR}`);
    }
  }

  get hashCode(): string {
    if (this.orientation === Orientation.Vertical) {
      return ReferenceLine.VERTICAL_INDICATOR + moment.utc(this.value).format(ReferenceLine.TIMESTAMP_FORMAT) + ReferenceLine.SEPARATOR + this.label;
    } else {
      return ReferenceLine.HORIZONTAL_INDICATOR + this.value + ReferenceLine.SEPARATOR + this.label;
    }
  }

  getLabel(): string {
    return this.label;
  }

  getOrientation(): Orientation {
    return this.orientation;
  }

  getRawValue(): number | TimestampStruct {
    return this.value;
  }

  /**
   * If the Reference Line is Vertical (i.e. it's value is a time stamp), as the time zone information is not stored, the time zone must
   * be provided to turn the value into an time stamp epoch. The time zone information is not stored so that if the time zone of a site
   * changes the 'nominal' value of the reference line does not change.
   *
   * For Horizontal Reference Lines the time zone does not need to be provided when calling this method.
   *
   * @param timezone a time zone to be used for converting the time zone agnostic value to a timestamp epoch. Must be supplied for
   * Vertical Reference Lines, and can be omitted (as it is not used) for Horizontal Reference Lines
   */
  getNumberValue(timezone?: string): number {
    if (this.getOrientation() === Orientation.Vertical) {
      if (!timezone) {
        throw new Error('Timezone must be provided to get the number value of the Vertical ReferenceLine');
      } else {
        return moment.tz(this.value, timezone).valueOf();
      }
    } else {
      return <number>this.value;
    }
  }
}
